import React, { useEffect, useState } from "react";
import qs from "qs";
import swal from "sweetalert2";
import Table from "../../common/table";
import SearchBox from "../../common/searchBox";
import Modal from "../../common/Modal";
import Icon from "../../icons/index";
import ReactTooltip from "react-tooltip";
import SingleStatusToggle from "../../common/singleStatusToggle";
import CS from "../../../services/commonService";
import { useStore } from "../../../zustand/store";
import { pageToLoadOnDelete } from "../../../helpers";
import { POINTS_PER_DOLLAR, formatDollars, formatPoints } from "./rewardConfig";
import {
	useGetAllRewardRules,
	useAddRewardRule,
	useUpdateRewardRule,
	useToggleRewardRuleStatus,
	useDeleteRewardRule,
} from "../../../queryHooks/useRewardRules";

// UI label <-> API value maps. The accounting-service schema expects
// SCREAMING_SNAKE_CASE enum values; the form shows human-readable labels.
const RULE_TYPE_OPTIONS = [
	{ label: "Order Based", value: "ORDER_BASED" },
	{ label: "Order Milestone", value: "ORDER_MILESTONE" },
	{ label: "New User", value: "NEW_USER" },
	{ label: "Referral", value: "REFERRAL" },
	{ label: "Spending Based", value: "SPENDING_BASED" },
	{ label: "Re-engagement", value: "RE_ENGAGEMENT" },
	{ label: "Promotional", value: "PROMOTIONAL" },
	{ label: "Custom", value: "CUSTOM" },
	{ label: "Redemption Rule", value: "REDEMPTION" },
];

const ORDER_TYPE_OPTIONS = [
	{ label: "All Orders", value: "ALL" },
	{ label: "Delivery", value: "DELIVERY" },
	{ label: "Pickup", value: "PICKUP" },
];

const TIME_PERIOD_OPTIONS = [
	{ label: "No Time Limit", value: "NONE" },
	{ label: "Daily", value: "DAILY" },
	{ label: "Weekly", value: "WEEKLY" },
	{ label: "Monthly", value: "MONTHLY" },
	{ label: "Yearly", value: "YEARLY" },
];

const FREQUENCY_OPTIONS = [
	{ label: "Every Time Condition Is Met", value: "EVERY_TIME" },
	{ label: "Once Per User", value: "ONCE_PER_USER" },
	{ label: "Once Per Day", value: "ONCE_PER_DAY" },
	{ label: "Once Per Month", value: "ONCE_PER_MONTH" },
];

// When a rule is evaluated, per type. The first option is the backend default for that type.
const TRIGGER_OPTIONS = {
	NEW_USER: [
		{ label: "Sign-up", value: "SIGNUP" },
		{ label: "First qualifying order", value: "ORDER_COMPLETED" },
	],
	REFERRAL: [{ label: "Referral", value: "REFERRAL" }],
	RE_ENGAGEMENT: [
		{ label: "Comeback order", value: "ORDER_COMPLETED" },
		{ label: "Login (coming soon)", value: "LOGIN" },
	],
	REDEMPTION: [{ label: "Checkout (redeem points)", value: "CHECKOUT" }],
};
const DEFAULT_TRIGGER_OPTIONS = [{ label: "Completed order", value: "ORDER_COMPLETED" }];
const triggerOptionsFor = (type) => TRIGGER_OPTIONS[type] || DEFAULT_TRIGGER_OPTIONS;
const ALL_TRIGGER_LABELS = { SIGNUP: "Sign-up", ORDER_COMPLETED: "Completed order", REFERRAL: "Referral", LOGIN: "Login", CHECKOUT: "Checkout" };

// What accounting-service evaluates today: sign-up rules, Order Based rules on completed orders,
// and redemption at checkout. Other rule types and triggers are saved but not evaluated yet.
const isLiveRule = (type, trigger) =>
	trigger === "SIGNUP" || (trigger === "ORDER_COMPLETED" && type === "ORDER_BASED") || (trigger === "CHECKOUT" && type === "REDEMPTION");

// Redemption rules spend points (e.g. 600 points = $10 off) instead of awarding them.
const isRedemption = (type) => type === "REDEMPTION";
const formatMoney = (amount) => {
	const value = Number(amount);
	return Number.isInteger(value) ? `${value}` : value.toFixed(2);
};
const redemptionSummary = (conditions = {}) =>
	`${formatPoints(conditions.pointsToRedeem)} = $${formatMoney(conditions.discountAmount)} off` +
	(conditions.minOrderValue ? ` (orders $${formatMoney(conditions.minOrderValue)}+)` : "");

const valueByLabel = (options, label) => options.find((o) => o.label === label)?.value;
const labelByValue = (options, value) => options.find((o) => o.value === value)?.label || "";
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");

// Condition rows shown in the View modal, keyed by condition field (TRIGGER_FIELDS order).
const CONDITION_LABELS = {
	requiredOrders: { label: "Required Orders", format: (v) => v },
	requiredSpend: { label: "Required Spend", format: (v) => `$${formatMoney(v)}` },
	inactiveMonths: { label: "Inactive For", format: (v) => `${v} month${Number(v) === 1 ? "" : "s"}` },
	customCondition: { label: "Condition", format: (v) => v },
	pointsToRedeem: { label: "Points per Block", format: (v) => formatPoints(v) },
	discountAmount: { label: "Discount per Block", format: (v) => `$${formatMoney(v)}` },
	minOrderValue: { label: "Minimum Order Value", format: (v) => `$${formatMoney(v)}` },
	maxBlocksPerOrder: { label: "Max Blocks per Order", format: (v) => v },
	orderType: { label: "Qualifying Order Type", format: (v) => labelByValue(ORDER_TYPE_OPTIONS, v) || v },
	timePeriod: { label: "Time Period", format: (v) => labelByValue(TIME_PERIOD_OPTIONS, v) || v },
};
const EMPTY_CONDITION_TEXT = {
	minOrderValue: "No minimum",
	maxBlocksPerOrder: "No limit",
	orderType: "All Orders",
	timePeriod: "No Time Limit",
};

// Read-only list of every field of a rule, for the View modal.
const ruleDetails = (rule) => {
	const conditions = rule.conditions || {};
	const redemption = isRedemption(rule.type);
	const conditionRows = (TRIGGER_FIELDS[rule.type] || []).map((field) => {
		const value = conditions[field];
		const empty = value === undefined || value === null || value === "";
		return {
			label: CONDITION_LABELS[field]?.label || field,
			value: empty ? EMPTY_CONDITION_TEXT[field] || "-" : CONDITION_LABELS[field].format(value),
		};
	});
	return [
		{ label: "Rule Name", value: rule.name },
		{ label: "Description", value: rule.description || "-" },
		{ label: "Type", value: labelByValue(RULE_TYPE_OPTIONS, rule.type) || rule.type },
		{ label: "Fires On", value: ALL_TRIGGER_LABELS[rule.trigger] || "-" },
		{ label: "Status", value: rule.isActive ? "Active" : "Inactive", status: true },
		{ heading: "Trigger / Conditions" },
		...(conditionRows.length ? conditionRows : [{ label: "Conditions", value: "None" }]),
		{ heading: redemption ? "Redemption" : "Points" },
		...(redemption
			? [{ label: "Redemption Value", value: redemptionSummary(conditions) }]
			: [
				{ label: "Points to Award", value: formatPoints(rule.points) },
				{ label: "Point Value", value: formatDollars(rule.points) },
				{ label: "Frequency", value: labelByValue(FREQUENCY_OPTIONS, rule.frequency) || rule.frequency || "-" },
			]),
		{ heading: "History" },
		{ label: "Created", value: formatDateTime(rule.createdAt) },
		...(rule.updatedAt ? [{ label: "Last Updated", value: formatDateTime(rule.updatedAt) }] : []),
	];
};

// Which trigger/condition fields each rule type uses, keyed by API type value.
const TRIGGER_FIELDS = {
	ORDER_BASED: ["minOrderValue", "orderType", "timePeriod"],
	ORDER_MILESTONE: ["requiredOrders", "minOrderValue", "orderType", "timePeriod"],
	NEW_USER: ["minOrderValue", "orderType"],
	REFERRAL: ["minOrderValue", "orderType"],
	SPENDING_BASED: ["requiredSpend", "orderType", "timePeriod"],
	RE_ENGAGEMENT: ["inactiveMonths"],
	PROMOTIONAL: ["minOrderValue", "orderType", "timePeriod"],
	CUSTOM: ["customCondition"],
	REDEMPTION: ["pointsToRedeem", "discountAmount", "minOrderValue", "maxBlocksPerOrder", "orderType"],
};

const EMPTY_FORM = {
	name: "",
	description: "",
	type: "",
	trigger: "",
	requiredOrders: "",
	minOrderValue: "",
	orderType: "All Orders",
	timePeriod: "No Time Limit",
	requiredSpend: "",
	inactiveMonths: "",
	customCondition: "",
	pointsToRedeem: "",
	discountAmount: "",
	maxBlocksPerOrder: "",
	pointsPerUnit: "",
	frequency: FREQUENCY_OPTIONS[0].label,
	status: true,
};

// Maps a reward rule fetched from the API into the form's label-based shape.
const ruleToForm = (rule) => {
	const conditions = rule.conditions || {};
	return {
		...EMPTY_FORM,
		id: rule.id,
		name: rule.name,
		description: rule.description || "",
		type: labelByValue(RULE_TYPE_OPTIONS, rule.type),
		trigger: rule.trigger || "",
		requiredOrders: conditions.requiredOrders ?? "",
		requiredSpend: conditions.requiredSpend ?? "",
		inactiveMonths: conditions.inactiveMonths ?? "",
		customCondition: conditions.customCondition ?? "",
		pointsToRedeem: conditions.pointsToRedeem ?? "",
		discountAmount: conditions.discountAmount ?? "",
		maxBlocksPerOrder: conditions.maxBlocksPerOrder ?? "",
		minOrderValue: conditions.minOrderValue ?? "",
		orderType: labelByValue(ORDER_TYPE_OPTIONS, conditions.orderType) || ORDER_TYPE_OPTIONS[0].label,
		timePeriod: labelByValue(TIME_PERIOD_OPTIONS, conditions.timePeriod) || TIME_PERIOD_OPTIONS[0].label,
		pointsPerUnit: rule.points,
		frequency: labelByValue(FREQUENCY_OPTIONS, rule.frequency) || FREQUENCY_OPTIONS[0].label,
		status: rule.isActive,
		createdAt: rule.createdAt,
	};
};

// Maps the form's label-based shape into the API's request body.
const formToPayload = (form) => {
	const type = valueByLabel(RULE_TYPE_OPTIONS, form.type);
	const fields = TRIGGER_FIELDS[type] || [];
	const conditions = {
		orderType: valueByLabel(ORDER_TYPE_OPTIONS, form.orderType),
		timePeriod: valueByLabel(TIME_PERIOD_OPTIONS, form.timePeriod),
	};
	if (fields.includes("requiredOrders")) conditions.requiredOrders = Number(form.requiredOrders);
	if (fields.includes("requiredSpend")) conditions.requiredSpend = Number(form.requiredSpend);
	if (fields.includes("inactiveMonths")) conditions.inactiveMonths = Number(form.inactiveMonths);
	if (fields.includes("customCondition")) conditions.customCondition = form.customCondition;
	if (fields.includes("pointsToRedeem")) conditions.pointsToRedeem = Number(form.pointsToRedeem);
	if (fields.includes("discountAmount")) conditions.discountAmount = Number(form.discountAmount);
	if (fields.includes("maxBlocksPerOrder") && form.maxBlocksPerOrder !== "" && form.maxBlocksPerOrder !== null) {
		conditions.maxBlocksPerOrder = Number(form.maxBlocksPerOrder);
	}
	if (fields.includes("minOrderValue") && form.minOrderValue !== "" && form.minOrderValue !== null) {
		conditions.minOrderValue = Number(form.minOrderValue);
	}
	// ORDER_MILESTONE / SPENDING_BASED / RE_ENGAGEMENT / CUSTOM don't accept orderType/timePeriod
	// unless the type also uses them per TRIGGER_FIELDS.
	if (!fields.includes("orderType")) delete conditions.orderType;
	if (!fields.includes("timePeriod")) delete conditions.timePeriod;

	return {
		name: form.name.trim(),
		description: form.description || "",
		type,
		// Only send a trigger the type accepts; otherwise the backend applies the type's default.
		...(triggerOptionsFor(type).some((o) => o.value === form.trigger) ? { trigger: form.trigger } : {}),
		conditions,
		// Redemption rules award no points; the backend stores 0 for them.
		...(isRedemption(type) ? {} : { points: Number(form.pointsPerUnit) }),
		// Frequency has no meaning when spending points (maxBlocksPerOrder limits redemption), so it's fixed.
		frequency: isRedemption(type) ? "EVERY_TIME" : valueByLabel(FREQUENCY_OPTIONS, form.frequency),
		isActive: !!form.status,
	};
};

const columnKey = "reward_rules";

const RewardRules = () => {
	const setLoading = useStore((state) => state.setLoading);

	const [searchTerm, setSearchTerm] = useState("");
	const [pageNow, setPageNow] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [tableData, setTableData] = useState({ records: [], total_items: 0, page: 1, items_per_page: 10 });

	const [selectedRule, setSelectedRule] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState("view"); // view, edit, add
	const [form, setForm] = useState(EMPTY_FORM);
	const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
	const triggerFields = TRIGGER_FIELDS[valueByLabel(RULE_TYPE_OPTIONS, form.type)] || [];

	const payload = {
		page: pageNow,
		limit: itemsPerPage,
		...(searchTerm ? { search: searchTerm } : {}),
	};

	const {
		data: dataGetRewardRules,
		isLoading,
		refetch,
	} = useGetAllRewardRules(qs.stringify(payload));

	useEffect(() => {
		if (dataGetRewardRules?.status === "success") {
			setTableData(dataGetRewardRules.data);
		}
	}, [dataGetRewardRules]);

	// Create
	const { mutateAsync: addRewardRule, error: errorAdd, data: dataAdd } = useAddRewardRule();
	useEffect(() => {
		if (dataAdd) {
			setLoading(false);
			if (dataAdd.status === "success") {
				setModalOpen(false);
				CS.showSuccessMessage(dataAdd.message);
				refetch();
			} else if (dataAdd.status === "fail") {
				CS.showErrorMessage(dataAdd.message);
			}
		}
	}, [dataAdd]);
	useEffect(() => {
		if (errorAdd) {
			setLoading(false);
			CS.showErrorMessage(errorAdd?.response?.data?.message || "Failed to create reward rule");
		}
	}, [errorAdd]);

	// Update
	const { mutateAsync: updateRewardRule, error: errorUpdate, data: dataUpdate } = useUpdateRewardRule();
	useEffect(() => {
		if (dataUpdate) {
			setLoading(false);
			if (dataUpdate.status === "success") {
				setModalOpen(false);
				CS.showSuccessMessage(dataUpdate.message);
				refetch();
			} else if (dataUpdate.status === "fail") {
				CS.showErrorMessage(dataUpdate.message);
			}
		}
	}, [dataUpdate]);
	useEffect(() => {
		if (errorUpdate) {
			setLoading(false);
			CS.showErrorMessage(errorUpdate?.response?.data?.message || "Failed to update reward rule");
		}
	}, [errorUpdate]);

	// Toggle status
	const { mutateAsync: toggleStatus, error: errorToggle, data: dataToggle } = useToggleRewardRuleStatus();
	useEffect(() => {
		if (dataToggle) {
			setLoading(false);
			if (dataToggle.status === "success") {
				CS.showSuccessMessage(dataToggle.message);
				refetch();
			} else if (dataToggle.status === "fail") {
				CS.showErrorMessage(dataToggle.message);
			}
		}
	}, [dataToggle]);
	useEffect(() => {
		if (errorToggle) {
			setLoading(false);
			CS.showErrorMessage(errorToggle?.response?.data?.message || "Failed to update status");
		}
	}, [errorToggle]);

	// Delete
	const { mutateAsync: deleteRewardRule, error: errorDelete, data: dataDelete } = useDeleteRewardRule();
	useEffect(() => {
		if (dataDelete) {
			setLoading(false);
			if (dataDelete.status === "success") {
				const _page = pageToLoadOnDelete(tableData?.total_items, 1, pageNow, itemsPerPage);
				setPageNow(_page);
				if (_page === pageNow) refetch();
				CS.showSuccessMessage(dataDelete.message);
			} else if (dataDelete.status === "fail") {
				CS.showErrorMessage(dataDelete.message);
			}
		}
	}, [dataDelete]);
	useEffect(() => {
		if (errorDelete) {
			setLoading(false);
			CS.showErrorMessage(errorDelete?.response?.data?.message || "Failed to delete reward rule");
		}
	}, [errorDelete]);

	const handleSubmit = (e) => {
		e.preventDefault();
		const missingPoints = isRedemption(valueByLabel(RULE_TYPE_OPTIONS, form.type))
			? !form.pointsToRedeem || !form.discountAmount
			: !form.pointsPerUnit;
		if (!form.name.trim() || !form.type || missingPoints) {
			CS.showErrorMessage("Please fill all required fields");
			return;
		}
		const body = formToPayload(form);
		setLoading(true);
		if (modalMode === "add") {
			addRewardRule(body);
		} else {
			updateRewardRule({ id: selectedRule.id, body });
		}
	};

	const columns = [
		{ label: "Rule Name", type: "string", path: "name", sort: true },
		{ label: "Type", type: "string", path: "type", content: (item) => labelByValue(RULE_TYPE_OPTIONS, item.type) },
		{ label: "Fires On", type: "string", path: "trigger", content: (item) => ALL_TRIGGER_LABELS[item.trigger] || "-" },
		{
			label: "Points/Unit",
			type: "number",
			path: "points",
			// Redemption rows show the points spent, not `points` (0), so this column can't sort on its path.
			sort: false,
			content: (item) => (isRedemption(item.type) ? `-${item.conditions?.pointsToRedeem ?? 0}` : item.points),
		},
		{
			label: "Value",
			type: "string",
			content: (item) =>
				isRedemption(item.type) ? redemptionSummary(item.conditions) : `${formatPoints(item.points)} = ${formatDollars(item.points)}`,
		},
		{
			label: "Status",
			type: "boolean",
			path: "isActive",
			content: (item) => (
				<SingleStatusToggle
					_id={`${item.id}${item.isActive}`}
					status={item.isActive}
					htmlFor={`${item.id}${item.isActive}`}
					onToggleChange={() => {
						setLoading(true);
						toggleStatus(item.id);
					}}
				/>
			),
		},
		{
			label: "Created",
			type: "string",
			path: "createdAt",
			sort: true,
			content: (item) => (item.createdAt ? item.createdAt.split("T")[0] : ""),
		},
		{
			label: "Actions",
			type: "string",
			content: (item) => (
				<div className="action-buttons">
					<Icon
						type="show"
						onClick={() => {
							setSelectedRule(item);
							setModalMode("view");
							setModalOpen(true);
						}}
						data-tip="View"
						title="View"
					/>
					<Icon
						type="edit"
						onClick={() => {
							setSelectedRule(item);
							setForm(ruleToForm(item));
							setModalMode("edit");
							setModalOpen(true);
						}}
						data-tip="Edit"
						title="Edit"
					/>
					<Icon
						type="delete"
						onClick={() =>
							swal
								.fire({
									title: "Are you sure you want to delete this rule?",
									icon: "warning",
									showCancelButton: true,
									confirmButtonColor: "#3085d6",
									cancelButtonColor: "#d33",
									confirmButtonText: "Yes, delete it!",
								})
								.then((result) => {
									if (result.isConfirmed) {
										setLoading(true);
										deleteRewardRule(item.id);
									}
								})
						}
						data-tip="Delete"
						title="Delete"
					/>
					<ReactTooltip />
				</div>
			),
		},
	];

	return (
		<div className="module-section">
			<div className="section-header">
				<h3>Reward & Redemption Rules</h3>
				<button
					className="btn btn-primary"
					onClick={() => {
						setSelectedRule(null);
						setForm(EMPTY_FORM);
						setModalMode("add");
						setModalOpen(true);
					}}
				>
					<i className="bx bx-plus-circle" style={{ marginRight: "6px" }} /> Add Rule
				</button>
			</div>

			<div className="section-filters">
				<SearchBox
					search={(value) => {
						setPageNow(1);
						setSearchTerm(value);
					}}
					searchValue={searchTerm}
				/>
			</div>

			<Table
				columns={columns}
				columnKey={columnKey}
				tableData={tableData}
				loading={isLoading}
				setCurrentPage={(page) => setPageNow(page)}
				setCurrentLimit={(limit) => {
					setPageNow(1);
					setItemsPerPage(limit);
				}}
			/>

			{modalOpen && (
				<Modal
					open={modalOpen}
					onCloseModal={() => setModalOpen(false)}
					showCloseIcon={true}
				>
					{modalMode === "view" && selectedRule ? (
						<div className="reward-rule-view">
							<h2>Rule Details</h2>
							<div className="rule-details">
								{ruleDetails(selectedRule).map((row) =>
									row.heading ? (
										<h4 key={row.heading} className="rule-details-heading">{row.heading}</h4>
									) : (
										<div key={row.label} className="rule-detail-row">
											<span className="rule-detail-label">{row.label}</span>
											<span className="rule-detail-value">
												{row.status ? (
													<span className={`rule-status ${selectedRule.isActive ? "active" : "inactive"}`}>{row.value}</span>
												) : (
													row.value
												)}
											</span>
										</div>
									)
								)}
							</div>
							<div className="rule-view-footer">
								<button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
									Close
								</button>
								<button
									type="button"
									className="btn btn-primary"
									onClick={() => {
										setForm(ruleToForm(selectedRule));
										setModalMode("edit");
									}}
								>
									Edit Rule
								</button>
							</div>
						</div>
					) : (
					<div style={{ padding: "20px", minWidth: "450px", maxHeight: "80vh", overflowY: "auto" }}>
						<h2 style={{ marginBottom: "20px", marginTop: 0 }}>
							{modalMode === "add" ? "Add New Reward Rule" : "Edit Reward Rule"}
						</h2>
						<form className="modal-form" onSubmit={handleSubmit}>
							<div className="form-group">
								<label>Rule Name *</label>
								<input type="text" className="form-control" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g., New User Reward" />
							</div>
							<div className="form-group">
								<label>Description</label>
								<textarea className="form-control" value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Describe how users earn points with this rule" rows="3" />
							</div>
							<div className="form-group">
								<label>Rule Type *</label>
								<select
									className="form-control"
									value={form.type}
									onChange={(e) => {
										setField("type", e.target.value);
										setField("trigger", triggerOptionsFor(valueByLabel(RULE_TYPE_OPTIONS, e.target.value))[0].value);
									}}
								>
									<option value="">Select Type</option>
									{RULE_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.label}>{t.label}</option>)}
								</select>
							</div>
							{form.type && (
								<div className="form-group">
									<label>Fires On *</label>
									<select className="form-control" value={form.trigger} onChange={(e) => setField("trigger", e.target.value)}>
										{triggerOptionsFor(valueByLabel(RULE_TYPE_OPTIONS, form.type)).map((t) => (
											<option key={t.value} value={t.value}>{t.label}</option>
										))}
									</select>
									<small className="text-muted">
										{isLiveRule(valueByLabel(RULE_TYPE_OPTIONS, form.type), form.trigger)
											? "Live: this rule is applied automatically when it fires."
											: "Not live yet: this rule can be saved, but it isn't applied to users yet."}
									</small>
								</div>
							)}

							{form.type && (
								<>
									<h4 className="form-subheading">Trigger / Conditions</h4>
									{triggerFields.includes("requiredOrders") && (
										<div className="form-group">
											<label>Required Orders *</label>
											<input type="number" min="1" className="form-control" value={form.requiredOrders} onChange={(e) => setField("requiredOrders", e.target.value)} placeholder="e.g., 10" />
										</div>
									)}
									{triggerFields.includes("requiredSpend") && (
										<div className="form-group">
											<label>Required Spend ($) *</label>
											<input type="number" min="1" className="form-control" value={form.requiredSpend} onChange={(e) => setField("requiredSpend", e.target.value)} placeholder="e.g., 100" />
										</div>
									)}
									{triggerFields.includes("inactiveMonths") && (
										<div className="form-group">
											<label>Inactive For (Months) *</label>
											<input type="number" min="1" className="form-control" value={form.inactiveMonths} onChange={(e) => setField("inactiveMonths", e.target.value)} placeholder="e.g., 6" />
										</div>
									)}
									{triggerFields.includes("customCondition") && (
										<div className="form-group">
											<label>Condition *</label>
											<textarea className="form-control" rows="2" value={form.customCondition} onChange={(e) => setField("customCondition", e.target.value)} placeholder="Describe the condition" />
										</div>
									)}
									{triggerFields.includes("pointsToRedeem") && (
										<div className="form-group">
											<label>Points to Redeem (per block) *</label>
											<input type="number" min="1" className="form-control" value={form.pointsToRedeem} onChange={(e) => setField("pointsToRedeem", e.target.value)} placeholder="e.g., 600" />
										</div>
									)}
									{triggerFields.includes("discountAmount") && (
										<div className="form-group">
											<label>Discount per Block ($) *</label>
											<input type="number" min="0.01" step="0.01" className="form-control" value={form.discountAmount} onChange={(e) => setField("discountAmount", e.target.value)} placeholder="e.g., 10" />
										</div>
									)}
									{triggerFields.includes("minOrderValue") && (
										<div className="form-group">
											<label>Minimum Order Value</label>
											<input type="number" min="0" className="form-control" value={form.minOrderValue} onChange={(e) => setField("minOrderValue", e.target.value)} placeholder="Optional" />
										</div>
									)}
									{triggerFields.includes("maxBlocksPerOrder") && (
										<div className="form-group">
											<label>Max Blocks per Order</label>
											<input type="number" min="1" className="form-control" value={form.maxBlocksPerOrder} onChange={(e) => setField("maxBlocksPerOrder", e.target.value)} placeholder="Optional (empty = no limit)" />
											<small className="text-muted">Each extra block of points gives another discount, up to this limit. Points are deducted only when the order payment succeeds.</small>
										</div>
									)}
									{triggerFields.includes("orderType") && (
										<div className="form-group">
											<label>Qualifying Order Type</label>
											<select className="form-control" value={form.orderType} onChange={(e) => setField("orderType", e.target.value)}>
												{ORDER_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.label}>{t.label}</option>)}
											</select>
										</div>
									)}
									{triggerFields.includes("timePeriod") && (
										<div className="form-group">
											<label>Time Period</label>
											<select className="form-control" value={form.timePeriod} onChange={(e) => setField("timePeriod", e.target.value)}>
												{TIME_PERIOD_OPTIONS.map((t) => <option key={t.value} value={t.label}>{t.label}</option>)}
											</select>
										</div>
									)}
								</>
							)}

							{isRedemption(valueByLabel(RULE_TYPE_OPTIONS, form.type)) ? (
								<div className="form-group">
									<label>Redemption Value</label>
									<div className="points-value-hint">
										{form.pointsToRedeem && form.discountAmount
											? redemptionSummary({ pointsToRedeem: form.pointsToRedeem, discountAmount: form.discountAmount, minOrderValue: form.minOrderValue })
											: "e.g., 600 Points = $10 off (orders $25+)"}
									</div>
								</div>
							) : (
								<>
									<div className="form-group">
										<label>Points to Award *</label>
										<input type="number" min="1" className="form-control" value={form.pointsPerUnit} onChange={(e) => setField("pointsPerUnit", e.target.value)} placeholder="e.g., 100" />
									</div>
									<div className="form-group">
										<label>Point Value</label>
										<div className="points-value-hint">
											{form.pointsPerUnit
												? `${formatPoints(form.pointsPerUnit)} = ${formatDollars(form.pointsPerUnit)}`
												: `${POINTS_PER_DOLLAR} Points = ${formatDollars(POINTS_PER_DOLLAR)}`}
										</div>
									</div>
								</>
							)}
							{!isRedemption(valueByLabel(RULE_TYPE_OPTIONS, form.type)) && (
								<div className="form-group">
									<label>Frequency *</label>
									<select className="form-control" value={form.frequency} onChange={(e) => setField("frequency", e.target.value)}>
										{FREQUENCY_OPTIONS.map((f) => <option key={f.value} value={f.label}>{f.label}</option>)}
									</select>
								</div>
							)}
							<div className="form-group">
								<label>
									<input type="checkbox" checked={form.status} onChange={(e) => setField("status", e.target.checked)} /> {form.status ? "Active" : "Inactive"}
								</label>
							</div>
							<div className="modal-footer">
								<button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
									Cancel
								</button>
								<button type="submit" className="btn btn-primary">
									{modalMode === "add" ? "Create Rule" : "Save Changes"}
								</button>
							</div>
						</form>
					</div>
					)}
				</Modal>
			)}
		</div>
	);
};

export default RewardRules;
